import { useContext, useEffect, useMemo, useState } from 'react';
import DetailsContext from '../../providers/DetailsContext';
import { THEME_COLOR_KEYS, THEME_GROUPS } from '../../variables/themeColorKeys';
import { parseCssColor, formatCssColor } from '../../utils/cssColor';
import GradientEditor from './GradientEditor';
import ColorPreview from './ColorPreview';
import IconSave from '../../components/icons/IconSave';
import language from '../../variables/language';

const GROUP_LABEL_KEYS = {
  backgrounds: 'groupBackgrounds',
  text: 'groupText',
  borders: 'groupBorders',
  accents: 'groupAccents',
};

function buildPalettes(lightPalette, darkPalette) {
  return { light: { ...lightPalette }, dark: { ...darkPalette } };
}

function ColorRow({ entry, value, onChange, label, allValues }) {
  return (
    <div className={'theme-editor-card' + (entry.rawText ? ' theme-editor-card-wide' : '')}>
      <div className="theme-editor-card-head">
        <ColorPreview kind={entry.preview} value={value} allValues={allValues} />
        <span className="theme-editor-card-label">{label}</span>
      </div>
      <div className="theme-editor-card-body">
        {entry.rawText ? (
          <GradientEditor value={value} onChange={onChange} />
        ) : (
          <FlatColorControls value={value} onChange={onChange} />
        )}
      </div>
    </div>
  );
}

function FlatColorControls({ value, onChange }) {
  const { hex, alpha } = parseCssColor(value);
  return (
    <>
      <input type="color" value={hex} onChange={(e) => onChange(formatCssColor(e.target.value, alpha))} />
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={alpha}
        onChange={(e) => onChange(formatCssColor(hex, Number(e.target.value)))}
      />
    </>
  );
}

export default function ThemeEditorPanel() {
  const { theme, lightPalette, setLightPalette, darkPalette, setDarkPalette, saveThemeToServer, themeSyncedAt } = useContext(DetailsContext);
  const langRoot = language[language.lang];
  const t = langRoot.themeSettings;

  const [formValues, setFormValues] = useState(() => buildPalettes(lightPalette, darkPalette));
  const [initialValues, setInitialValues] = useState(() => buildPalettes(lightPalette, darkPalette));
  const [saving, setSaving] = useState(false);

  const mode = theme === 'true' ? 'light' : 'dark';
  const isDirty = useMemo(() => JSON.stringify(formValues) !== JSON.stringify(initialValues), [formValues, initialValues]);

  // Layout.jsx fetches the saved palette from the server asynchronously,
  // after this panel has already mounted with whatever was in localStorage/
  // the manifest defaults. themeSyncedAt only changes when that fetch
  // actually resolves (not on every local edit here, unlike lightPalette/
  // darkPalette themselves, which this panel's own onChange also writes to)
  // - pull the fresh values in when it does, but only while there's no
  // unsaved local edit in progress, so an in-flight edit can't be silently
  // clobbered by a network response.
  useEffect(() => {
    if (isDirty || themeSyncedAt === 0) return;
    const fresh = buildPalettes(lightPalette, darkPalette);
    setFormValues(fresh);
    setInitialValues(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeSyncedAt]);

  const handleColorChange = (key, cssValue) => {
    setFormValues((prev) => ({ ...prev, [mode]: { ...prev[mode], [key]: cssValue } }));
    if (mode === 'light') {
      setLightPalette((prev) => ({ ...prev, [key]: cssValue }));
    } else {
      setDarkPalette((prev) => ({ ...prev, [key]: cssValue }));
    }
  };

  const handleDiscard = () => {
    setFormValues(initialValues);
    setLightPalette(initialValues.light);
    setDarkPalette(initialValues.dark);
  };

  const handleSave = () => {
    if (saving) return;
    setSaving(true);
    saveThemeToServer(formValues.light, formValues.dark)
      .then(() => setInitialValues(formValues))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const activeValues = formValues[mode];

  return (
    <div className="settings-section theme-editor-panel">
      <h3>{t.title}</h3>
      <p className="theme-editor-mode-hint">{mode === 'light' ? t.editingLight : t.editingDark}</p>
      {THEME_GROUPS.map((group) => (
        <div className="theme-editor-group" key={group}>
          <h4 className="theme-editor-group-title">{t[GROUP_LABEL_KEYS[group]]}</h4>
          <div className="theme-editor-grid">
            {THEME_COLOR_KEYS.filter((entry) => entry.group === group).map((entry) => (
              <ColorRow
                key={entry.key}
                entry={entry}
                value={activeValues[entry.key]}
                allValues={activeValues}
                label={t[entry.key] || entry.key}
                onChange={(value) => handleColorChange(entry.key, value)}
              />
            ))}
          </div>
        </div>
      ))}
      {isDirty ? (
        <div className="theme-editor-form-footer">
          <span className="theme-editor-unsaved-hint">{langRoot.unsavedChanges}</span>
          <div className="theme-editor-discard-link" onClick={handleDiscard}>{langRoot.cancel}</div>
          <button type="button" className="theme-editor-save-btn" disabled={saving} onClick={handleSave}>
            <IconSave />
            {saving ? langRoot['Loading ...'] : langRoot.save}
          </button>
        </div>
      ) : (
        ''
      )}
    </div>
  );
}
